import { Alert } from 'antd';
import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import styles from '../../Snapshots/SnapshotDrilldown.module.css';

export interface MetricDataPoint {
  date: string;
  value: number;
}

export interface MetricField {
  [key: string]: MetricDataPoint[];
}

export interface MetricTarget {
  [key: string]:
    | MetricField
    | Record<string, MetricDataPoint[]>
    | { pct_not_null: MetricDataPoint[] }
    | { count: MetricDataPoint[] }
    | undefined;
  topN?: Record<string, MetricDataPoint[]>;
  exists?: { pct_not_null: MetricDataPoint[] };
  count?: { count: MetricDataPoint[] };
}

export interface MetricsNamespace {
  [key: string]: MetricTarget;
}

export interface MetricsTS {
  'data:meta'?: MetricsNamespace;
  'pages:field'?: MetricsNamespace;
  'data:field'?: MetricsNamespace;
}

interface GraphConfig {
  id: string;
  title: string;
  ns: string;
  target: string;
  name: string;
  measures: string[];
  addTopN?: string | boolean;
}

const graphConfigs: GraphConfig[] = [
  { id: 'rows-graph', title: 'Rows', ns: 'data:meta', target: 'rows', name: 'count', measures: ['count'], addTopN: true },
  {
    id: 'status-code-graph',
    title: 'Status codes',
    ns: 'pages:field',
    target: 'error',
    name: 'exists',
    measures: [],
    addTopN: 'statusCode',
  },
  {
    id: 'validation-error-graph',
    title: 'Validation Errors - Rows without errors',
    ns: 'data:meta',
    target: '_validationErrors',
    name: 'topN/arraySize',
    measures: ['0'],
    addTopN: true,
  },
  {
    id: 'error-graph',
    title: 'Error %',
    ns: 'pages:field',
    target: 'error',
    name: 'exists',
    measures: ['pct_not_null'],
    addTopN: true,
  },
  {
    id: 'retry-graph',
    title: 'Average Attempts per input',
    ns: 'data:meta',
    target: '_screenCapture',
    name: 'arraySize',
    measures: ['avg'],
  },
  {
    id: 'avail-graph',
    title: 'Availability',
    ns: 'data:field',
    target: 'available',
    name: 'booleanValue',
    measures: ['pct_true'],
  },
  {
    id: 'price-graph',
    title: 'List Price Low',
    ns: 'data:field',
    target: 'listPriceLow',
    name: 'numberValue',
    measures: ['min', 'p01', 'p05', 'p25', 'p50', 'p75', 'p95', 'p99', 'max'],
    addTopN: 'listPriceLowCurrency',
  },
];

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#ffb347', '#83d483', '#ff6b9d'];

function prepareGraphData(config: GraphConfig, metricsTS: MetricsTS): any[] {
  try {
    const ns = metricsTS[config.ns as keyof MetricsTS];
    if (!ns) return [];

    const targetData = ns[config.target];
    if (!targetData) return [];

    const labels = targetData.exists?.pct_not_null?.map((x) => x.date) || targetData.count?.count?.map((x) => x.date) || [];

    const topN =
      typeof config.addTopN === 'string' && ns[config.addTopN]
        ? ns[config.addTopN].topN
        : config.addTopN === true
          ? targetData.topN
          : undefined;

    const fieldData = targetData[config.name];
    if (!fieldData && !topN) return [];

    return labels.map((date) => {
      const dataPoint: any = { date: date };

      config.measures.forEach((measure) => {
        if (fieldData && fieldData[measure]) {
          const el = fieldData[measure].find((pt: MetricDataPoint) => pt.date === date);
          dataPoint[measure] = el ? el.value : 0;
        }
      });

      if (topN) {
        Object.keys(topN).forEach((key) => {
          const el = topN[key].find((pt: MetricDataPoint) => pt.date === date);
          dataPoint[key] = el ? el.value : 0;
        });
      }

      return dataPoint;
    });
  } catch (error) {
    console.error(`Error preparing graph data for ${config.id}:`, error);
    return [];
  }
}

function GraphChart({ config, metricsTS }: { config: GraphConfig; metricsTS: MetricsTS }) {
  const data = prepareGraphData(config, metricsTS);

  if (data.length === 0) {
    return null;
  }

  const dataKeys = Object.keys(data[0]).filter((key) => key !== 'date');
  const measureKeys = config.measures;
  const topNKeys = dataKeys.filter((key) => !measureKeys.includes(key));

  return (
    <div className={styles.graph} style={{ marginBottom: 32 }}>
      <h3 style={{ marginBottom: 16, textAlign: 'center' }}>{config.title}</h3>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" style={{ fontSize: 10 }} />
          <YAxis yAxisId="left" style={{ fontSize: 10 }} />
          {topNKeys.length > 0 && <YAxis yAxisId="right" orientation="right" style={{ fontSize: 10 }} />}
          <Tooltip
            offset={20}
            allowEscapeViewBox={{ x: true, y: true }}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '8px',
            }}
            wrapperStyle={{
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {measureKeys.map((measure, index) => (
            <Line
              key={measure}
              yAxisId="left"
              type="monotone"
              dataKey={measure}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
          {topNKeys.map((key, index) => (
            <Bar key={key} yAxisId="right" dataKey={key} stackId="topN" fill={COLORS[index % COLORS.length]} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface MetricsGraphsProps {
  metricsTS: MetricsTS | null;
  error: string | null;
}

export function MetricsGraphs({ metricsTS, error }: MetricsGraphsProps) {
  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
        {error && (
          <Alert
            type="warning"
            showIcon
            description={
              <div>
                <strong>Metrics Unavailable</strong>
                <p>{error}</p>
              </div>
            }
          />
        )}
        {metricsTS &&
          graphConfigs.map(
            (config) =>
              // Hide retry graph by default as in original template
              config.id !== 'retry-graph' && <GraphChart key={config.id} config={config} metricsTS={metricsTS} />,
          )}
        {!metricsTS && !error && <Alert type="info" showIcon description="No metrics data available" />}
      </div>
    </div>
  );
}
